import type {
  CompleteVideoResponse,
  CompletedPart,
  CreateVideoBody,
  CreateVideoResponse,
  UploadPart,
} from '../lib/types';

/**
 * Upload-while-recording.
 *
 * MediaRecorder hands us chunks every few seconds. We accumulate them until we
 * have at least one S3 part's worth (5 MB — the multipart minimum for every
 * part but the last), cut a part, and PUT it to its presigned URL while the
 * recording carries on. By the time somebody hits stop, everything but the
 * tail is already in S3, which is why Loom's "your video is ready" feels
 * instant. Parts are numbered and independently retryable: a dropped
 * connection costs one part, never the recording.
 *
 * The one external requirement is CORS on the raw bucket:
 *
 *     ExposeHeaders: ["ETag"]
 *
 * Without it `response.headers.get('ETag')` is null in the browser and the
 * multipart upload cannot be completed. That is detected and reported as
 * exactly that, rather than as a mystery failure.
 */

/** S3's minimum part size for every part except the last. */
export const PART_SIZE = 5 * 1024 * 1024;

/** How many part URLs to ask for at a time. */
const PART_BATCH = 5;

/** Refill when fewer than this many unused URLs remain. */
const PART_REFILL_THRESHOLD = 2;

const MAX_PARALLEL_UPLOADS = 2;
const MAX_ATTEMPTS = 5;

export interface UploadTransport {
  createVideo(body: CreateVideoBody): Promise<CreateVideoResponse>;
  moreParts(videoId: string, fromPartNumber: number, count: number): Promise<UploadPart[]>;
  complete(
    videoId: string,
    body: { parts: CompletedPart[]; durationSec?: number; title?: string },
  ): Promise<CompleteVideoResponse>;
  discard(videoId: string): Promise<void>;
}

export type UploadPhase = 'idle' | 'starting' | 'uploading' | 'finishing' | 'done' | 'failed';

export interface UploadState {
  phase: UploadPhase;
  videoId: string | null;
  shareId: string | null;
  /** Bytes handed to S3 and acknowledged. */
  uploadedBytes: number;
  /** Bytes the recorder has produced so far. */
  capturedBytes: number;
  partsUploaded: number;
  partsInFlight: number;
  /** Set when a part has exhausted its retries; recoverable via `retry()`. */
  error: string | null;
}

const INITIAL_STATE: UploadState = {
  phase: 'idle',
  videoId: null,
  shareId: null,
  uploadedBytes: 0,
  capturedBytes: 0,
  partsUploaded: 0,
  partsInFlight: 0,
  error: null,
};

interface QueuedPart {
  partNumber: number;
  blob: Blob;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export class StreamingUpload {
  private state: UploadState = { ...INITIAL_STATE };
  private readonly transport: UploadTransport;
  private readonly onChange: (state: UploadState) => void;

  private session: CreateVideoResponse | null = null;
  private sessionPromise: Promise<CreateVideoResponse> | null = null;

  private pending: Blob[] = [];
  private pendingBytes = 0;
  private nextPartNumber = 1;

  private readonly partUrls = new Map<number, string>();
  private readonly etags = new Map<number, string>();
  private queue: QueuedPart[] = [];
  private readonly failedParts = new Map<number, Blob>();
  private inFlight = 0;
  private pumping = false;
  private refilling: Promise<void> | null = null;

  private aborted = false;

  constructor(transport: UploadTransport, onChange: (state: UploadState) => void) {
    this.transport = transport;
    this.onChange = onChange;
  }

  get snapshot(): UploadState {
    return this.state;
  }

  /**
   * Open the upload. Deliberately not awaited by the caller: recording starts
   * the instant the user asks for it and the first chunks buffer in memory
   * until the session exists, usually well under a second later.
   */
  start(body: CreateVideoBody): Promise<CreateVideoResponse> {
    this.patch({ phase: 'starting' });
    this.sessionPromise = this.transport
      .createVideo(body)
      .then((session) => {
        if (this.aborted) return session;
        this.session = session;
        for (const part of session.parts) this.partUrls.set(part.partNumber, part.url);
        this.patch({
          phase: 'uploading',
          videoId: session.videoId,
          shareId: session.shareId,
        });
        void this.pump();
        return session;
      })
      .catch((error: unknown) => {
        this.patch({ phase: 'failed', error: describe(error) });
        throw error;
      });
    return this.sessionPromise;
  }

  /** Feed a MediaRecorder chunk in. Cheap and synchronous. */
  push(chunk: Blob): void {
    if (this.aborted || chunk.size === 0) return;
    this.pending.push(chunk);
    this.pendingBytes += chunk.size;
    this.patch({ capturedBytes: this.state.capturedBytes + chunk.size });

    if (this.pendingBytes >= PART_SIZE) this.cutPart();
  }

  /**
   * Flush the tail, wait for every part to land, and tell the API the upload
   * is complete. Resolves with the API's view of the finished video.
   */
  async finish(options: { durationSec?: number; title?: string }): Promise<CompleteVideoResponse> {
    if (this.aborted) throw new Error('This recording was discarded.');
    this.patch({ phase: 'finishing' });

    // The final part may be smaller than 5 MB; S3 allows exactly that for the
    // last part of a multipart upload.
    if (this.pendingBytes > 0) this.cutPart();

    const session = await this.ensureSession();
    await this.drain();

    if (this.failedParts.size > 0) {
      const message = this.state.error ?? 'Some parts of this recording could not be uploaded.';
      this.patch({ phase: 'failed', error: message });
      throw new Error(message);
    }
    if (this.etags.size === 0) {
      const message = 'Nothing was captured, so there is nothing to upload.';
      this.patch({ phase: 'failed', error: message });
      throw new Error(message);
    }

    const parts: CompletedPart[] = [...this.etags.entries()]
      .map(([partNumber, etag]) => ({ partNumber, etag }))
      .sort((a, b) => a.partNumber - b.partNumber);

    try {
      const completed = await this.transport.complete(session.videoId, {
        parts,
        ...(options.durationSec !== undefined ? { durationSec: options.durationSec } : {}),
        ...(options.title ? { title: options.title } : {}),
      });
      this.patch({ phase: 'done', error: null });
      return completed;
    } catch (error) {
      this.patch({ phase: 'failed', error: describe(error) });
      throw error;
    }
  }

  /** Re-queue everything that failed. Safe to call repeatedly. */
  async retry(): Promise<void> {
    if (this.failedParts.size === 0) return;
    for (const [partNumber, blob] of this.failedParts) {
      this.queue.push({ partNumber, blob });
    }
    this.failedParts.clear();
    this.patch({ phase: 'uploading', error: null });
    void this.pump();
    await this.drain();
  }

  /**
   * Throw the whole thing away. Used by restart and discard: the API aborts
   * the S3 multipart upload so the orphaned parts do not linger and bill.
   */
  abort(): void {
    if (this.aborted) return;
    this.aborted = true;
    this.queue = [];
    this.pending = [];
    this.pendingBytes = 0;
    this.failedParts.clear();

    const videoId = this.session?.videoId;
    if (videoId) {
      void this.transport.discard(videoId).catch(() => {
        // Best effort. The API's retention sweep reaps abandoned uploads.
      });
    }
    this.patch({ ...INITIAL_STATE, phase: 'idle' });
  }

  // -------------------------------------------------------------------------

  /**
   * Turn everything buffered into one part. Called when the buffer crosses
   * 5 MB, and once more at the end for the tail — a part may run past 5 MB,
   * which S3 is happy with (the ceiling is 5 GB) and which means chunk
   * boundaries never have to be split.
   */
  private cutPart(): void {
    if (this.pending.length === 0) return;

    const blob = new Blob(this.pending);
    this.pending = [];
    this.pendingBytes = 0;

    const partNumber = this.nextPartNumber;
    this.nextPartNumber += 1;
    this.queue.push({ partNumber, blob });
    void this.pump();
  }

  private async ensureSession(): Promise<CreateVideoResponse> {
    if (this.session) return this.session;
    if (!this.sessionPromise) throw new Error('The upload was never started.');
    return this.sessionPromise;
  }

  private async pump(): Promise<void> {
    if (this.pumping || this.aborted) return;
    this.pumping = true;
    try {
      while (!this.aborted && this.queue.length > 0 && this.inFlight < MAX_PARALLEL_UPLOADS) {
        const next = this.queue.shift();
        if (!next) break;
        this.inFlight += 1;
        this.patch({ partsInFlight: this.inFlight });
        void this.sendPart(next).finally(() => {
          this.inFlight -= 1;
          this.patch({ partsInFlight: this.inFlight });
          void this.pump();
        });
      }
    } finally {
      this.pumping = false;
    }
  }

  private async sendPart(part: QueuedPart): Promise<void> {
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS && !this.aborted; attempt += 1) {
      try {
        // A presigned URL can expire mid-recording on a long session, so
        // re-sign from the second attempt onwards rather than retrying a URL
        // that will never work again.
        const url = await this.urlFor(part.partNumber, attempt > 1);
        const response = await fetch(url, { method: 'PUT', body: part.blob });

        if (!response.ok) {
          lastError = new Error(`S3 rejected part ${part.partNumber} (${response.status}).`);
        } else {
          const etag = response.headers.get('ETag') ?? response.headers.get('etag');
          if (!etag) {
            // Not retryable: this is a bucket misconfiguration, not a blip.
            this.failPart(
              part,
              'The storage bucket is not exposing the ETag header to the browser. ' +
                'Add ExposeHeaders: ["ETag"] to the raw bucket CORS rules.',
            );
            return;
          }
          this.etags.set(part.partNumber, etag);
          this.patch({
            uploadedBytes: this.state.uploadedBytes + part.blob.size,
            partsUploaded: this.etags.size,
            error: null,
          });
          return;
        }
      } catch (error) {
        lastError = error;
      }

      if (attempt < MAX_ATTEMPTS) {
        // 0.4s, 0.8s, 1.6s, 3.2s — plus jitter, so a flaky network does not
        // resynchronise every part onto the same retry beat.
        await sleep(400 * 2 ** (attempt - 1) + Math.random() * 250);
      }
    }

    if (!this.aborted) this.failPart(part, describe(lastError));
  }

  private failPart(part: QueuedPart, message: string): void {
    this.failedParts.set(part.partNumber, part.blob);
    this.patch({ error: message });
  }

  private async urlFor(partNumber: number, forceRefresh: boolean): Promise<string> {
    // Wait for the session before consulting the cache: POST /v1/videos comes
    // back with the first batch of signed URLs, and the first parts are often
    // cut before it resolves. Checking first would spend a needless round trip
    // asking for URLs we are about to be handed.
    const session = await this.ensureSession();

    if (!forceRefresh) {
      const cached = this.partUrls.get(partNumber);
      if (cached) {
        void this.maybeRefill(partNumber);
        return cached;
      }
    }

    const count = forceRefresh ? 1 : PART_BATCH;
    const parts = await this.transport.moreParts(session.videoId, partNumber, count);
    for (const part of parts) this.partUrls.set(part.partNumber, part.url);

    const url = this.partUrls.get(partNumber);
    if (!url) throw new Error(`The API did not return an upload URL for part ${partNumber}.`);
    return url;
  }

  /** Keep a small buffer of signed URLs ahead of the recorder. */
  private async maybeRefill(currentPartNumber: number): Promise<void> {
    if (this.refilling || this.aborted) return;

    let ahead = 0;
    for (let candidate = currentPartNumber + 1; this.partUrls.has(candidate); candidate += 1) {
      ahead += 1;
      if (ahead > PART_REFILL_THRESHOLD) return;
    }

    const session = this.session;
    if (!session) return;
    const from = currentPartNumber + ahead + 1;

    this.refilling = this.transport
      .moreParts(session.videoId, from, PART_BATCH)
      .then((parts) => {
        for (const part of parts) this.partUrls.set(part.partNumber, part.url);
      })
      .catch(() => {
        // Non-fatal: sendPart will fetch a URL on demand and surface the error
        // there, where it can be retried.
      })
      .finally(() => {
        this.refilling = null;
      });

    await this.refilling;
  }

  /** Resolve once nothing is queued or in flight. */
  private async drain(): Promise<void> {
    while (!this.aborted && (this.queue.length > 0 || this.inFlight > 0)) {
      await sleep(120);
    }
  }

  private patch(patch: Partial<UploadState>): void {
    this.state = { ...this.state, ...patch };
    this.onChange(this.state);
  }
}

function describe(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'The upload failed.';
}
